import type { SeoPageContent } from "./types";

export const vehicleInspectionSoftware: SeoPageContent = {
  path: "/vehicle-inspection-software",
  metaTitle: "Vehicle Inspection Software for Transport Operators | TransportMate",
  metaDescription:
    "Vehicle inspection software for collections and deliveries. Structured digital walk-rounds with photographs, damage records, signatures and automatic PDF reports.",
  eyebrow: "Inspections",
  h1: "Vehicle Inspection Software",
  standfirst: "A consistent digital walk-round at both ends of every movement.",
  intro: [
    "A vehicle inspection is only useful if it is done the same way every time. Paper sheets drift: one driver photographs everything, another ticks a box, and six weeks later nobody can tell what the vehicle looked like when it was collected.",
    "TransportMate turns the inspection into a guided workflow in the driver app, so the same points are covered on every job and the evidence lands against the right movement automatically.",
  ],
  sections: [
    {
      heading: "What a digital vehicle inspection covers",
      bullets: [
        { title: "Guided walk-round", desc: "The driver is taken through the inspection step by step rather than deciding what to record." },
        { title: "Photographic coverage", desc: "Photographs captured in the app at the vehicle, attached to the job as they are taken." },
        { title: "Damage recording", desc: "Damage marked against the vehicle with supporting photographs." },
        { title: "AI damage detection", desc: "Condition descriptions with severity ratings generated from the damage photographs." },
        { title: "Signature", desc: "Captured on the device from the person releasing or receiving the vehicle." },
        { title: "Time and location", desc: "Recorded against the completed inspection." },
        { title: "PDF report", desc: "The finished inspection produced as a document automatically." },
      ],
    },
    {
      heading: "Collection and delivery inspections",
      body: [
        "The same inspection structure is used at collection and at delivery. That is what makes the records comparable: if a mark appears on the delivery inspection that was not on the collection inspection, it is visible immediately rather than being argued about.",
      ],
    },
    {
      heading: "Inspections without a connection",
      body: [
        "Inspections are frequently completed in compounds, ports and underground car parks with no usable signal. In TransportMate the full inspection — photographs, damage, signature — is captured on the device and uploads automatically once connectivity returns. The driver can finish the job and move on.",
      ],
    },
    {
      heading: "Daily vehicle checks",
      body: [
        "Alongside collection and delivery inspections, TransportMate supports daily checks on your own vehicles, so the condition of the transporter itself is recorded as part of routine operations rather than on a separate paper pad.",
      ],
    },
  ],
  faqs: [
    { q: "How does vehicle inspection software work?", a: "The driver follows a guided inspection in the app, photographing the vehicle and recording any damage. The completed inspection, signature, time and location are saved against the job and produced as a PDF report." },
    { q: "Can vehicle transport software capture photos?", a: "Yes. Photographs are taken inside the TransportMate app during the inspection and attached to the job automatically." },
    { q: "Can inspections be completed offline?", a: "Yes. The full inspection can be completed without a connection; it syncs automatically when the device is back online." },
    { q: "Does the software produce an inspection report?", a: "Yes. TransportMate generates a PDF report from the completed inspection." },
  ],
  related: [
    { to: "/vehicle-damage-inspection-software", label: "Vehicle damage inspection software", desc: "Recording damage with markers and photographic evidence." },
    { to: "/vehicle-condition-report-software", label: "Vehicle condition report software", desc: "The report the inspection produces." },
    { to: "/features/vehicle-inspection", label: "Inspections in TransportMate", desc: "How the inspection feature works in the product." },
    { to: "/poc-pod-software", label: "POC and POD software", desc: "How inspections become proof of collection and delivery." },
    { to: "/vehicle-transport-driver-app", label: "Vehicle transport driver app", desc: "The driver's side of the workflow." },
    { to: "/solutions/vehicle-transport-software", label: "Vehicle transport software", desc: "The wider platform inspections form part of." },
  ],
  breadcrumbs: [{ name: "Vehicle inspection software", to: "/vehicle-inspection-software" }],
  softwareApplication: true,
  ctaHeading: "Make every inspection consistent",
  ctaBody: "Run a collection and a delivery inspection in TransportMate and see the report it produces.",
};

export const vehicleDamageInspectionSoftware: SeoPageContent = {
  path: "/vehicle-damage-inspection-software",
  metaTitle: "Vehicle Damage Inspection Software | TransportMate",
  metaDescription:
    "Vehicle damage inspection software for transport operators. Mark damage on the vehicle, attach photographs as evidence and compare collection with delivery.",
  eyebrow: "Damage evidence",
  h1: "Vehicle Damage Inspection Software",
  standfirst: "Record where the damage is, not just that there is some.",
  intro: [
    "\"Scratch on nearside\" is not evidence. A damage record only helps when it says where the damage is, how severe it is, and shows it in a photograph taken at the vehicle.",
    "TransportMate records damage against the vehicle with supporting photographs, so a claim can be answered with the actual images rather than a description written in a hurry.",
  ],
  sections: [
    {
      heading: "How damage is captured",
      bullets: [
        { title: "Damage markers", desc: "Damage is recorded against a specific point on the vehicle rather than as a general note." },
        { title: "Photographs linked to the damage", desc: "The supporting images are attached to the damage they show, so the evidence stays together." },
        { title: "Additional photographs", desc: "Extra images can be captured and associated with the damage being recorded." },
        { title: "AI damage detection", desc: "TransportMate generates condition descriptions with severity ratings from the damage photographs, as an assistance to the driver's own record." },
        { title: "Captured at the vehicle", desc: "Photographs are taken in the app during the inspection rather than uploaded later." },
      ],
    },
    {
      heading: "Comparing collection with delivery",
      body: [
        "Damage recorded at collection becomes the baseline. When the delivery inspection is completed, both records sit against the same job, so new damage stands out against what was already there. That comparison is the whole point of documenting collections properly.",
      ],
    },
    {
      heading: "Why photographic evidence changes the outcome",
      body: [
        "Damage claims are usually settled on the strength of the evidence rather than the strength of the argument. An operator who can produce dated, located photographs of the vehicle as collected is in a materially different position to one producing a tick sheet.",
        "It also protects drivers. Damage that was present before the vehicle was loaded stops becoming their problem.",
      ],
    },
  ],
  faqs: [
    { q: "How does vehicle damage inspection software work?", a: "During the inspection the driver records damage against the vehicle and photographs it in the app. The damage record and its images are saved to the job and included in the proof document." },
    { q: "Can damage photographs be linked to a specific point on the vehicle?", a: "Yes. TransportMate records damage as markers and keeps the supporting photographs attached to the damage they show." },
    { q: "Does TransportMate use AI for damage?", a: "TransportMate can generate condition descriptions with severity ratings from damage photographs. It supports the driver's inspection rather than replacing it." },
    { q: "How do I prove damage happened in transit?", a: "By comparing the collection and delivery records for the same job. If damage appears on the delivery inspection and not on the collection inspection, the records show it." },
  ],
  related: [
    { to: "/vehicle-inspection-software", label: "Vehicle inspection software", desc: "The wider inspection the damage record sits inside." },
    { to: "/vehicle-condition-report-software", label: "Vehicle condition report software", desc: "How damage appears in the finished report." },
    { to: "/proof-of-collection-software", label: "Proof of collection software", desc: "Recording existing damage before the movement starts." },
    { to: "/best-poc-pod-software-uk", label: "Best POC & POD software UK", desc: "What to look for when comparing systems." },
  ],
  breadcrumbs: [{ name: "Vehicle damage inspection software", to: "/vehicle-damage-inspection-software" }],
  ctaHeading: "Evidence that answers the claim",
  ctaBody: "See how damage markers and photographs appear in a completed TransportMate report.",
};

export const vehicleConditionReportSoftware: SeoPageContent = {
  path: "/vehicle-condition-report-software",
  metaTitle: "Vehicle Condition Report Software | TransportMate",
  metaDescription:
    "Vehicle condition report software that turns a completed inspection into a professional PDF report with photographs, damage detail, signatures, time and location.",
  eyebrow: "Reporting",
  h1: "Vehicle Condition Report Software",
  standfirst: "The inspection is the work. The report is what the customer sees.",
  intro: [
    "A vehicle condition report is the document that summarises an inspection: what the vehicle looked like, what damage was recorded, who signed, and when and where it happened. It is what gets sent to the customer and what gets produced if a claim is made.",
    "TransportMate generates the report automatically from the completed inspection, so nobody in the office assembles it by hand.",
  ],
  sections: [
    {
      heading: "What the report contains",
      bullets: [
        { title: "Vehicle and job detail", desc: "The vehicle, the movement it belongs to and the customer it was carried out for." },
        { title: "Inspection results", desc: "The structured condition record completed by the driver." },
        { title: "Photographs", desc: "The images captured at the vehicle during the inspection." },
        { title: "Damage detail", desc: "Damage recorded during the inspection with its supporting photographs." },
        { title: "Signature", desc: "The signature taken on the device at the handover." },
        { title: "Time and location", desc: "When and where the inspection was completed." },
      ],
    },
    {
      heading: "Automatic generation and delivery",
      body: [
        "The report is produced as a PDF when the inspection is completed, and the finished proof can be sent on to the customer without the office chasing the driver for paperwork. Reports stay attached to the job, so they can be found again later without searching through email.",
      ],
    },
    {
      heading: "Why an automatic report matters",
      body: [
        "Manual reporting is where the time goes. Someone downloads photographs, pastes them into a template, types up the damage notes and emails it out. Doing that for every movement is a job in itself, and it is the first thing to slip when the operation gets busy — usually on the day you most need the record.",
      ],
    },
  ],
  faqs: [
    { q: "What is a vehicle condition report?", a: "A document recording the condition of a vehicle at a point in time, including the inspection results, photographs, any damage, a signature, and the time and location of the inspection." },
    { q: "Can vehicle transport software generate PDF reports?", a: "Yes. TransportMate produces the condition report as a PDF automatically when the inspection is completed." },
    { q: "Can the report be sent to the customer?", a: "Yes. The completed proof document can be sent on to the customer as part of the job workflow." },
    { q: "Are reports kept against the job?", a: "Yes. Reports remain attached to the job they belong to, so collection and delivery documentation stays together." },
  ],
  related: [
    { to: "/vehicle-inspection-software", label: "Vehicle inspection software", desc: "The inspection the report is generated from." },
    { to: "/vehicle-damage-inspection-software", label: "Vehicle damage inspection software", desc: "How damage is recorded and evidenced." },
    { to: "/poc-pod-software", label: "POC and POD software", desc: "Reports as proof of collection and delivery." },
    { to: "/vehicle-delivery-software", label: "Vehicle delivery software", desc: "Where reporting fits in the delivery workflow." },
    { to: "/solutions/vehicle-transport-software", label: "Vehicle transport software", desc: "Condition reporting as part of the whole transport workflow." },
  ],
  breadcrumbs: [{ name: "Vehicle condition report software", to: "/vehicle-condition-report-software" }],
  ctaHeading: "Reports without the assembly work",
  ctaBody: "Complete an inspection in TransportMate and look at the document it produces.",
};
